import React, { useState, useMemo } from 'react';
import type { Department, StaffMember, Shift, Schedule, AdminUser, DayOfWeek, AttendanceRecord, AttendanceRequest, WorkloadFactors } from '../../types';
import ScheduleCell from './ScheduleCell';
import { Icons } from '../ui/Icons';
import { getWeekStartDate, getWeekDates, formatDateYYYYMMDD, formatDayAndDate, formatDateRange } from '../../utils/dateUtils';
import { DAY_INDEX_MAP } from '../../types';
import StaffProfileModal from '../staff/StaffProfileModal';
import PrintOptionsModal from './PrintOptionsModal';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

interface SchedulerPreviewViewProps {
    departments: Department[];
    people: SchedulablePerson[];
    shifts: Shift[];
    schedule: Schedule;
    weekStartDay: DayOfWeek;
    onInitiatePrint: (data: any) => void;
    workloadFactors?: WorkloadFactors;
    isAdmin?: boolean;
    isSuperAdmin?: boolean;
    attendanceRecords?: AttendanceRecord[];
    attendanceRequests?: AttendanceRequest[];
    onUpdateAttendance?: (staffId: string, date: string, updates: { actualStartTime: string, actualEndTime: string, staffNote?: string, adminNote?: string }) => void;
    onCreateAttendanceRequest?: (staffId: string, date: string, updates: { requestedStartTime: string, requestedEndTime: string, staffNote?: string }) => void;
    showMetrics?: boolean;
}

const dayHeaderStyles = [
    'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',       // Monday
    'bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300', // Tuesday
    'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',   // Wednesday
    'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300',   // Thursday
    'bg-teal-50 text-teal-800 dark:bg-teal-900/20 dark:text-teal-300',     // Friday
    'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',     // Saturday
    'bg-indigo-50 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300', // Sunday
];

const dayHeaderBorderStyles = [
    'border-t-4 border-red-200 dark:border-red-700',
    'border-t-4 border-orange-200 dark:border-orange-700',
    'border-t-4 border-amber-200 dark:border-amber-700',
    'border-t-4 border-green-200 dark:border-green-700',
    'border-t-4 border-teal-200 dark:border-teal-700',
    'border-t-4 border-blue-200 dark:border-blue-700',
    'border-t-4 border-indigo-200 dark:border-indigo-700',
];

const WeekNavigator: React.FC<{
    currentWeekStartDate: Date;
    onPrevious: () => void;
    onNext: () => void;
    onToday: () => void;
    onPrint: () => void;
}> = ({ currentWeekStartDate, onPrevious, onNext, onToday, onPrint }) => (
    <div className="flex items-center justify-between mb-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-3 rounded-xl shadow-sm">
        <div className="flex items-center gap-3">
             <div className="flex items-center gap-1 bg-white dark:bg-slate-700/50 p-1 rounded-lg border border-gray-200 dark:border-slate-700">
                <button onClick={onPrevious} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300"><Icons.ChevronLeft className="h-5 w-5" /></button>
                <button onClick={onNext} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300"><Icons.ChevronRight className="h-5 w-5" /></button>
            </div>
            <button onClick={onToday} className="px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">Today</button>
        </div>
        <div className="flex items-center gap-4 text-right">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                {formatDateRange(currentWeekStartDate)}
            </h2>
            <button 
                onClick={onPrint}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all shadow-sm active:scale-95"
            >
                <Icons.Printer className="w-4 h-4" />
                Print
            </button>
        </div>
    </div>
);

const SchedulerPreviewView: React.FC<SchedulerPreviewViewProps> = ({ 
    departments, 
    people, 
    shifts, 
    schedule, 
    weekStartDay, 
    onInitiatePrint, 
    isAdmin, 
    isSuperAdmin, 
    attendanceRecords = [], 
    attendanceRequests = [],
    onUpdateAttendance,
    onCreateAttendanceRequest,
    showMetrics = true
}) => {
    const startDayIndex = DAY_INDEX_MAP[weekStartDay];
    const [currentWeekStartDate, setCurrentWeekStartDate] = useState(() => getWeekStartDate(new Date(), startDayIndex));
    const [selectedStaffForProfile, setSelectedStaffForProfile] = useState<SchedulablePerson | null>(null);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

    const weekDates = useMemo(() => getWeekDates(currentWeekStartDate), [currentWeekStartDate]);

    const goToPreviousWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() - 7);
        return newDate;
    });

    const goToNextWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + 7);
        return newDate;
    });
    
    const goToToday = () => setCurrentWeekStartDate(getWeekStartDate(new Date(), startDayIndex));

    const handlePrint = (departmentId: string) => {
        onInitiatePrint({
            departments,
            people,
            shifts,
            schedule,
            weekStartDate: currentWeekStartDate,
            departmentId,
        });
        setIsPrintModalOpen(false);
    };

    const departmentStaffMap = useMemo(() => {
        const map = new Map<string, SchedulablePerson[]>();
        departments.forEach(dep => map.set(dep.id, []));
        people.forEach(person => {
            if (person.departmentIds && person.departmentIds.length > 0) {
                const primaryDepId = person.departmentIds[0];
                if (map.has(primaryDepId)) {
                    map.get(primaryDepId)?.push(person);
                }
            }
        });
        return map;
    }, [departments, people]);

    const selectedStaffDepartment = useMemo(() => {
        if (!selectedStaffForProfile?.departmentIds || selectedStaffForProfile.departmentIds.length === 0) return undefined;
        return departments.find(d => d.id === selectedStaffForProfile.departmentIds[0]);
    }, [selectedStaffForProfile, departments]);

    const staffWeeklyHours = useMemo(() => {
        const hours: { [staffId: string]: number } = {};
        const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));
        for (const person of people) {
            let personHours = 0;
            for (const date of weekDates) {
                const dateString = formatDateYYYYMMDD(date);
                const entry = schedule[person.id]?.[dateString];
                if (entry?.status === 'ASSIGNED' && entry.shiftId) {
                    const shift = shiftsMap.get(entry.shiftId);
                    if (shift) {
                        const start = new Date(`1970-01-01T${shift.startTime}:00`);
                        const end = new Date(`1970-01-01T${shift.endTime}:00`);
                        if (end < start) { // Handles overnight shifts
                            end.setDate(end.getDate() + 1);
                        }
                        personHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                    }
                }
            }
            hours[person.id] = personHours;
        }
        return hours;
    }, [schedule, people, shifts, weekDates]);

    const departmentHours = useMemo(() => {
        const daily: { [date: string]: { [depId: string]: number } } = {};
        const weekly: { [depId: string]: number } = {};
        
        // Initialize weekly totals for all departments
        departments.forEach(dep => {
            weekly[dep.id] = 0;
        });

        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            daily[dateString] = {};
            departments.forEach(dep => {
                daily[dateString][dep.id] = 0;
            });
        });

        const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule) {
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED' && entry.shiftId) {
                        const shift = shiftsMap.get(entry.shiftId);
                        if (shift) {
                            // Grouping logic: prioritize the departmentId on the entry itself
                            const p = person as any;
                            const deptIds = Array.isArray(p.departmentIds) ? p.departmentIds : (typeof p.departmentIds === 'string' ? [p.departmentIds] : (p.departmentId ? [p.departmentId] : []));
                            const depId = entry.departmentId || deptIds.find(id => departments.some(d => d.id === id)) || deptIds[0];

                            if (depId && daily[dateString] && typeof daily[dateString][depId] === 'number') {
                                const start = new Date(`1970-01-01T${shift.startTime}:00`);
                                const end = new Date(`1970-01-01T${shift.endTime}:00`);
                                if (end < start) {
                                    end.setDate(end.getDate() + 1);
                                }
                                const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                                daily[dateString][depId] += duration;
                                weekly[depId] += duration;
                            }
                        }
                    }
                });
            }
        });

        return { daily, weekly };
    }, [schedule, people, shifts, weekDates, departments]);

    return (
        <div className="flex flex-col gap-6">
             {selectedStaffForProfile && (
                <StaffProfileModal
                    staff={selectedStaffForProfile as StaffMember}
                    departments={departments}
                    onClose={() => setSelectedStaffForProfile(null)}
                />
             )}
             {isPrintModalOpen && (
                <PrintOptionsModal
                    departments={departments}
                    onClose={() => setIsPrintModalOpen(false)}
                    onPrint={handlePrint}
                />
             )}
            <div className="flex-1 overflow-x-auto">
                 <WeekNavigator 
                    currentWeekStartDate={currentWeekStartDate}
                    onPrevious={goToPreviousWeek}
                    onNext={goToNextWeek}
                    onToday={goToToday}
                    onPrint={() => setIsPrintModalOpen(true)}
                />
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-md overflow-x-auto">
                    <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-y-auto max-h-[calc(100vh-200px)]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700 table-fixed">
                            <thead className="bg-gray-50 dark:bg-slate-900/50 sticky top-0 z-30 shadow-sm">
                                <tr>
                                    <th scope="col" className="sticky left-0 bg-gray-50 dark:bg-slate-900/50 z-40 px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-48 border-r border-gray-200 dark:border-slate-700">
                                        Staff Member
                                    </th>
                                    {weekDates.map((date, index) => {
                                        const { day, date: dateNum } = formatDayAndDate(date);
                                        return (
                                            <th key={date.toISOString()} scope="col" className={`px-2 py-2 text-center w-32 ${dayHeaderStyles[index]} ${dayHeaderBorderStyles[index]} bg-gray-50 dark:bg-slate-900/50 z-30`}>
                                                <div className="text-xl font-bold">{dateNum}</div>
                                                <div className="text-[10px] font-semibold uppercase tracking-wider">{day}</div>
                                            </th>
                                        )
                                    })}
                                    <th scope="col" className="sticky right-0 bg-gray-50 dark:bg-slate-900/50 z-40 px-4 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 border-l border-gray-200 dark:border-slate-700">
                                        Hours
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                                {departments.map(dep => (
                                    <React.Fragment key={dep.id}>
                                        <tr className="bg-gray-200 dark:bg-slate-700 sticky top-[57px] z-20 shadow-sm">
                                            <td className="sticky left-0 bg-gray-200 dark:bg-slate-700 z-30 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 w-48 border-r border-gray-300 dark:border-slate-600" style={{ color: dep.color }}>
                                                {dep.name}
                                            </td>
                                            {weekDates.map(date => {
                                                const dateString = formatDateYYYYMMDD(date);
                                                return (
                                                    <td key={dateString} className="px-3 py-2 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-slate-700 z-20">
                                                        <span className="text-[10px] opacity-70">{(departmentHours.daily[dateString]?.[dep.id] || 0).toFixed(1)} hrs</span>
                                                    </td>
                                                )
                                            })}
                                            <td className="sticky right-0 bg-gray-200 dark:bg-slate-700 z-30 px-4 py-2 w-24 border-l border-gray-300 dark:border-slate-600 text-center text-sm font-bold text-indigo-700 dark:text-indigo-300">
                                                {(departmentHours.weekly[dep.id] || 0).toFixed(1)}
                                            </td>
                                        </tr>
                                        {departmentStaffMap.get(dep.id)?.map(person => (
                                            <tr key={person.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 group">
                                                <td className="sticky left-0 bg-white dark:bg-slate-800 group-hover:bg-gray-50 dark:group-hover:bg-slate-700/50 z-20 px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 w-48 border-r border-gray-200 dark:border-slate-700" style={{ borderLeft: `4px solid ${person.color}` }}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="truncate">
                                                            {person.name}
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                                                                <span className="truncate max-w-[100px]">{person.role}</span>
                                                                <button 
                                                                    onClick={() => setSelectedStaffForProfile(person)}
                                                                    className="p-1 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all shadow-sm border border-indigo-100 dark:border-indigo-800/50"
                                                                    title="View Staff Profile"
                                                                >
                                                                    <Icons.ExternalLink className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                {weekDates.map(date => {
                                                    const dateString = formatDateYYYYMMDD(date);
                                                    return (
                                                        <ScheduleCell
                                                            key={`${person.id}-${dateString}`}
                                                            staffId={person.id}
                                                            date={dateString}
                                                            entry={schedule[person.id]?.[dateString]}
                                                            shifts={shifts}
                                                            personName={person.name}
                                                            readOnly={true}
                                                            allDepartments={departments}
                                                            staffDepartments={departments.filter(d => person.departmentIds?.includes(d.id))}
                                                            attendanceRecords={attendanceRecords}
                                                            attendanceRequests={attendanceRequests}
                                                            onUpdateAttendance={onUpdateAttendance}
                                                            onCreateAttendanceRequest={onCreateAttendanceRequest}
                                                            isAdmin={isAdmin}
                                                            isSuperAdmin={isSuperAdmin}
                                                        />
                                                    )
                                                })}
                                                <td className="sticky right-0 bg-white dark:bg-slate-800 group-hover:bg-gray-50 dark:group-hover:bg-slate-700/50 z-20 px-4 py-2 whitespace-nowrap text-center text-sm font-bold text-indigo-600 dark:text-indigo-400 w-24 border-l border-gray-200 dark:border-slate-700">
                                                    {(staffWeeklyHours[person.id] ?? 0).toFixed(1)}
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchedulerPreviewView;
